// react
import { useState } from 'react'

// external libs
import { Box, Button, Typography } from '@material-ui/core'
import { Add } from '@material-ui/icons'
import arrayMutators from 'final-form-arrays'
import { Form } from 'react-final-form'
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays'
import { FormApi } from 'final-form'

// components
import DocumentInfoField from './DocumentInfoField'

// hooks
import useLoading from '../../../../../control/loading/useLoading'
import useOfficeFile, {
    DocumentInfo,
    DocumentInfoReqeust,
    OfficeFile,
    OfficeFileRequest,
} from '../../../../../services/web/useOfficeFile'
import useTempFileService from '../../../../../services/web/useTempFileService'
import useSnackbarAction from '../../../../../stores/snackbar/snackbarAction'

export interface OfficeFilesFormProps {
    projectID: number
    initialValues?: OfficeFile
}

export default function OfficeFilesForm(props: OfficeFilesFormProps) {
    const { projectID, initialValues } = props

    const loadingHook = useLoading()
    const snackbarAction = useSnackbarAction()
    const officeFileServices = useOfficeFile()
    const tempFileServices = useTempFileService()

    const [fileTokensToDelete, setFileTokensToDelete] = useState<string[]>([])

    function handleAdd(fields: FieldArrayRenderProps<DocumentInfo, HTMLElement>['fields']) {
        fields.push({ fileInfo: [], title: '', remark: '' })
    }

    function handleRemove(index: number, fields: FieldArrayRenderProps<DocumentInfo, HTMLElement>['fields']) {
        if (fields.value[index] && fields.value[index].fileInfo.length > 0) {
            const fileToken = fields.value[index].fileInfo[0].fileToken
            setFileTokensToDelete((prev) => [...prev, fileToken])
        }

        fields.remove(index)
    }

    function getOfficeFileRequest(officeFile: OfficeFile): OfficeFileRequest {
        const documentInfo: DocumentInfoReqeust[] = officeFile.documentInfo.map((e) => ({
            title: e.title,
            remark: e.remark,
            fileTokenInfo: e.fileInfo.map((e) => e.fileToken),
        }))
        return { projectID, documentInfo }
    }

    async function updateOfficeFile(values: OfficeFile, form: FormApi<OfficeFile, Partial<any>>) {
        try {
            loadingHook.loadingOpen()

            const officeFileRequest = getOfficeFileRequest(values)
            const { status } = await officeFileServices.updateOfficeFile(officeFileRequest)

            if (status !== 1) {
                throw new Error('Fail')
            }

            if (fileTokensToDelete.length > 0) {
                const deleteFileTempReqeust = {
                    fileToken: fileTokensToDelete,
                }
                await tempFileServices.deleteTempFile(deleteFileTempReqeust)
            }

            snackbarAction.openSnackbar({
                message: 'บันทึกเอกสารสำเร็จ',
                type: 'success',
            })
            form.reset(values)
        } catch (error: any) {
            snackbarAction.openSnackbar({
                message: 'บันทึกไม่สำเร็จกรุณาลองใหม่อีกครั้ง',
                type: 'error',
            })
        } finally {
            loadingHook.loadingClose()
        }
    }

    return (
        <Form
            onSubmit={updateOfficeFile}
            initialValues={initialValues}
            mutators={{ ...arrayMutators }}
            render={({ values, handleSubmit, submitting, pristine }) => (
                <form style={{ width: '100%' }} onSubmit={handleSubmit}>
                    <FieldArray name="documentInfo">
                        {({ fields }) => (
                            <>
                                <Button
                                    size="large"
                                    variant="contained"
                                    color="primary"
                                    startIcon={<Add />}
                                    onClick={() => handleAdd(fields)}
                                >
                                    เพิ่มรายการเอกสาร
                                </Button>

                                <Box display="flex" flexDirection="column" mt={4} mb={4} style={{ gap: 32 }}>
                                    {fields.map((name, index) => (
                                        <DocumentInfoField
                                            key={index}
                                            no={index + 1}
                                            name={name}
                                            documentInfo={values.documentInfo[index]}
                                            onDelete={() => handleRemove(index, fields)}
                                            onDeleteTempFile={(e) => setFileTokensToDelete((prev) => [...prev, e])}
                                        />
                                    ))}

                                    {fields.length === 0 ? (
                                        <Box>
                                            <Typography>ไม่มีเอกสารที่อัปโหลด</Typography>
                                        </Box>
                                    ) : null}
                                </Box>
                            </>
                        )}
                    </FieldArray>

                    <Box display="flex" justifyContent="flex-end">
                        <Button color="primary" type="submit" variant="contained" disabled={submitting || pristine}>
                            บันทึก
                        </Button>
                    </Box>
                </form>
            )}
        />
    )
}
